import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";

import Icon from "./icons";
import { NavSection } from "./nav-config";
import VersionBadge from "../../components/VersionBadge";
import styles from "./Sidebar.module.scss";

export interface SidebarCompany {
  name: string;
  /**
   * Small caption above `name`. Defaults to "Company", which is right for a
   * portal scoped to one organisation (the supplier shell). The admin portal
   * has no company, so it puts the product name here and the signed-in user
   * in `name` — see PortalRouteLayout.
   */
  label?: string;
  /** Optional logo image; falls back to initials on a brand-coloured tile. */
  logoUrl?: string;
  initials?: string;
}

export interface SidebarUser {
  name: string;
  email?: string;
  avatarUrl?: string;
}

interface SidebarProps {
  company: SidebarCompany;
  user: SidebarUser;
  nav: NavSection[];
  /** Footer call-to-action button. Pass `null` to hide it (e.g. on the admin portal). */
  createCta?: { label: string; to: string } | null;
  /** Where the footer sign-out control links to. */
  logoutTo?: string;
  /** Called when any nav link is activated (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const initialsOf = (name: string) =>
  name
    .replace(/[^A-Za-z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "S";

// ── Collapsible section state (persisted per-origin) ──────────────────────────
const NAV_COLLAPSE_KEY = "lux.nav.collapsed";

const loadCollapsed = (): Set<string> => {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(NAV_COLLAPSE_KEY) : null;
    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set<string>();
  }
};

const saveCollapsed = (keys: Set<string>) => {
  try {
    localStorage.setItem(NAV_COLLAPSE_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    /* ignore (private mode / disabled storage) */
  }
};

const isItemActive = (pathname: string, to?: string) => {
  if (!to || to === "#") return false;
  return pathname === to || pathname.startsWith(`${to}/`);
};

const Sidebar = ({
  company,
  user,
  nav,
  createCta = { label: "Create New Offer", to: "/supplier/offers/new" },
  logoutTo = "/auth/logout",
  onNavigate,
}: SidebarProps) => {
  // Tile initials track the caption, not `name`: where the caption is the
  // product ("Verati Admin" → VA) the tile is the portal badge, so it must not
  // flip to the signed-in user's initials.
  const companyInitials = company.initials || initialsOf(company.label || company.name);

  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  // Section holding the current route — auto-expanded so the active item is
  // never hidden inside a folded section (the user can still re-collapse it).
  const activeSectionKey = useMemo(
    () => nav.find((s) => s.items.some((it) => isItemActive(pathname, it.to)))?.key,
    [nav, pathname]
  );

  useEffect(() => {
    if (!activeSectionKey) return;
    setCollapsed((prev) => {
      if (!prev.has(activeSectionKey)) return prev;
      const next = new Set(prev);
      next.delete(activeSectionKey);
      saveCollapsed(next);
      return next;
    });
  }, [activeSectionKey]);

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveCollapsed(next);
      return next;
    });

  return (
    <div className={styles.sidebar}>
      {/* ── Brand header ───────────────────────────────────────────── */}
      <div className={styles.brand}>
        <span className={styles.brandLogo}>
          {company.logoUrl ? (
            <img src={company.logoUrl} alt="" className={styles.brandLogoImg} />
          ) : (
            companyInitials
          )}
        </span>
        <div className={styles.brandText}>
          <span className={styles.brandLabel}>{company.label || "Company"}</span>
          <span className={styles.brandName}>{company.name}</span>
        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────────── */}
      <div className={styles.search}>
        <Icon name="search" size={15} className={styles.searchIcon} />
        <input className={styles.searchInput} type="text" placeholder="Search" />
        <span className={styles.searchKbd}>
          <span className={styles.searchKbdCmd}>⌘</span>F
        </span>
      </div>

      {/* ── Navigation ─────────────────────────────────────────────── */}
      <nav className={styles.nav} aria-label="Primary">
        {nav.map((section) => {
          const open = !collapsed.has(section.key);
          return (
            <div className={styles.navSection} key={section.key}>
              <button
                type="button"
                className={styles.navTitle}
                onClick={() => toggleSection(section.key)}
                aria-expanded={open}
              >
                <span>{section.title}</span>
                <Icon
                  name="chevron-down"
                  size={14}
                  className={`${styles.navTitleChevron}${open ? "" : ` ${styles.navTitleChevronCollapsed}`}`}
                />
              </button>
              {open && (
                <ul className={styles.navList}>
                  {section.items.map((item) => (
                    <li key={item.key}>
                      <NavLink
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ""}`
                        }
                      >
                        <span className={styles.navIcon}>
                          <Icon name={item.icon} size={16} />
                        </span>
                        <span className={styles.navText}>{item.label}</span>
                        {item.badge != null && (
                          <span className={styles.navBadge}>{item.badge}</span>
                        )}
                        {item.hasChildren && (
                          <Icon name="chevron-down" size={16} className={styles.navArrow} />
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <div className={styles.footer}>
        {createCta && (
          <Link to={createCta.to} className={styles.createBtn} onClick={onNavigate}>
            <Icon name="plus" size={16} />
            {createCta.label}
          </Link>
        )}
        <div className={styles.userRow}>
          <Link
            to="/account/profile"
            className={styles.userInfoLink}
            title="Your profile"
            onClick={onNavigate}
          >
            <span className={styles.userAvatar}>
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className={styles.userAvatarImg} />
              ) : (
                initialsOf(user.name).slice(0, 1)
              )}
            </span>
            <div className={styles.userText}>
              <span className={styles.userName}>{user.name}</span>
              {user.email && <span className={styles.userEmail}>{user.email}</span>}
            </div>
          </Link>
          <Link
            to={logoutTo}
            className={styles.logoutBtn}
            title="Sign out"
            aria-label="Sign out"
          >
            <Icon name="logout" size={16} />
          </Link>
        </div>
        <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 10px" }}>
          <VersionBadge />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
